import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeDetailDrawer } from "../../components/EmployeeDetailDrawer.js";
import { AppLayout } from "../../components/layout/AppLayout.js";
import { Header } from "../../components/layout/Header.js";
import { aiApi, employeesApi } from "../../lib/api.js";
import { Departments } from "./Departments.js";
import { KpiHeader } from "./KpiHeader.js";
import { PatternPanel } from "./PatternPanel.js";
import { type RiskListKind, RiskListModal, matchesRiskListKind } from "./RiskListModal.js";
import { TrendAndRisks } from "./TrendAndRisks.js";

export function Cockpit() {
  const { t } = useTranslation();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [riskListKind, setRiskListKind] = useState<RiskListKind | null>(null);

  const empQuery = useQuery({
    queryKey: ["employees"],
    queryFn: employeesApi.getAll,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const trendQuery = useQuery({
    queryKey: ["risk-trend"],
    queryFn: employeesApi.getRiskTrend,
    staleTime: 10 * 60_000,
  });
  const teamsQuery = useQuery({
    queryKey: ["teams"],
    queryFn: employeesApi.getTeams,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const costQuery = useQuery({
    queryKey: ["turnover-costs"],
    queryFn: () => employeesApi.getTurnoverCosts(),
    staleTime: 10 * 60_000,
    retry: 0,
  });
  const patternsQuery = useQuery({
    queryKey: ["ai-patterns"],
    queryFn: aiApi.getPatterns,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const refreshing = empQuery.isFetching || teamsQuery.isFetching;
  const refreshAll = () => {
    void empQuery.refetch();
    void trendQuery.refetch();
    void teamsQuery.refetch();
    void costQuery.refetch();
  };

  return (
    <AppLayout>
      <Header title={t("cockpit.title")} subtitle={t("cockpit.subtitle")} />
      <div className="flex-1 overflow-y-auto px-6 py-5 page-enter">
        <div className="flex justify-end mb-3">
          <button
            type="button"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium row-hover"
            style={{
              background: "transparent",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              color: "var(--text-muted)",
            }}
            onClick={refreshAll}
          >
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
            {t("cockpit.refresh")}
          </button>
        </div>

        <KpiHeader
          summary={empQuery.data?.summary}
          employees={empQuery.data?.employees ?? []}
          trend={trendQuery.data}
          cost={costQuery.data}
          loading={empQuery.isLoading}
          onOpenList={setRiskListKind}
        />
        <TrendAndRisks
          trend={trendQuery.data}
          employees={empQuery.data?.employees ?? []}
          loading={empQuery.isLoading}
          onSelectEmployee={setSelectedEmployeeId}
        />
        <Departments teamsQuery={teamsQuery} />
        <PatternPanel query={patternsQuery} />
      </div>

      {riskListKind && (
        <RiskListModal
          kind={riskListKind}
          employees={(empQuery.data?.employees ?? []).filter((e) =>
            matchesRiskListKind(e, riskListKind),
          )}
          onClose={() => setRiskListKind(null)}
          onSelect={(id) => {
            setRiskListKind(null);
            setSelectedEmployeeId(id);
          }}
        />
      )}
      {/* key = Personenwechsel setzt den lokalen Drawer-State zurück — siehe MeinTeam.tsx */}
      <EmployeeDetailDrawer
        key={selectedEmployeeId}
        employeeId={selectedEmployeeId}
        onClose={() => setSelectedEmployeeId(null)}
      />
    </AppLayout>
  );
}
